import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../services/api"; // Ensure this path is correct for your axios instance
import { getToken } from '../utils/auth';
import {
  ArrowLeftIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  ArrowsPointingInIcon,
  ArrowPathIcon,
  BookOpenIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";

// Import react-pdf components
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
// Worker is configured once in index.js

export default function AdminCopyViewer() {
  const { copyId } = useParams();
  const [copy, setCopy] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const [acCurrentPage, setAcCurrentPage] = useState(1);
  const [numAcPages, setNumAcPages] = useState(null);
  const DEFAULT_ZOOM = 1.05;
  const [acZoomLevel, setAcZoomLevel] = useState(DEFAULT_ZOOM);
  const [isAcLoading, setIsAcLoading] = useState(true);

  const ZOOM_STEP = 0.2;
  const MIN_ZOOM = 0.5;
  const MAX_ZOOM = 3.0;

  // Reopen copy state
  const [isReopening, setIsReopening] = useState(false);

  // --- Data Fetching and PDF Load Handlers ---
  useEffect(() => {
    const fetchCopyDetails = async () => {
      setIsLoading(true);
      setError("");
      try {
        const res = await api.get(`/admin/copies/view/${copyId}`);
        setCopy(res.data);
      } catch (err) {
        console.error("Error fetching copy details:", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch copy details."
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchCopyDetails();
  }, [copyId]);

  const onAcDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumAcPages(numPages);
    setIsAcLoading(false);
  }, []);

  const onAcDocumentLoadError = useCallback((err) => {
    console.error("Error loading Answer Copy PDF:", err);
    setIsAcLoading(false);
    setError(
      "Failed to load Answer Copy PDF. Please check the file format or link."
    );
  }, []);

  const pageWrapperRef = useRef(null);

  // Reset zoom on page change
  useEffect(() => {
    setAcZoomLevel(DEFAULT_ZOOM);
  }, [acCurrentPage]);

  const handleZoom = useCallback((action) => {
    setAcZoomLevel((prevZoom) => {
      let newZoom = prevZoom;
      if (action === "in") {
        newZoom = Math.min(MAX_ZOOM, prevZoom + ZOOM_STEP);
      } else if (action === "out") {
        newZoom = Math.max(MIN_ZOOM, prevZoom - ZOOM_STEP);
      } else if (action === "reset") {
        newZoom = DEFAULT_ZOOM;
      }
      return parseFloat(newZoom.toFixed(2));
    });
  }, []);

  // Reopen copy for re-evaluation
  const handleReopenCopy = async () => {
    if (!window.confirm("Are you sure you want to reopen this copy for re-evaluation? The examiner will be able to edit it again.")) {
      return;
    }

    setIsReopening(true);
    try {
      const res = await api.patch(`/admin/copies/${copyId}/reopen`);
      alert(res.data.message || "Copy successfully reopened!");
      
      // Refresh copy data
      const updatedCopy = await api.get(`/admin/copies/view/${copyId}`);
      setCopy(updatedCopy.data);
    } catch (err) {
      console.error("Error reopening copy:", err);
      alert(err.response?.data?.message || "Failed to reopen copy. Please try again.");
    } finally {
      setIsReopening(false);
    }
  };

  // --- Render Logic ---

  // Memoized stable file props — must be before any early returns (hooks rules)
  const acPdfUrl = copy?.driveFile?.id ? `/api/drive/pdf/${copy.driveFile.id}` : '';
  const qpPdfUrl = copy?.questionPaper?.driveFile?.id ? `/api/drive/pdf/${copy.questionPaper.driveFile.id}` : '';
  const acFileProp = useMemo(
    () => acPdfUrl ? { url: acPdfUrl, httpHeaders: { Authorization: `Bearer ${getToken()}` } } : null,
    [acPdfUrl] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const qpFileProp = useMemo(
    () => qpPdfUrl ? { url: qpPdfUrl, httpHeaders: { Authorization: `Bearer ${getToken()}` } } : null,
    [qpPdfUrl] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [loadingPdfs, setLoadingPdfs] = useState(new Set());

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-white" style={{fontFamily: 'Dosis, sans-serif'}}>
        <div className="flex flex-col items-center text-gray-600 text-lg">
          <ArrowPathIcon className="animate-spin h-10 w-10 text-gray-900" />
          <p className="mt-4 font-bold">Loading copy details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-gray-900 text-center py-10 text-2xl font-bold bg-white border-2 border-gray-900 rounded-lg mx-auto my-10 p-6" style={{fontFamily: 'Dosis, sans-serif'}}>
        Error: {error}
      </div>
    );
  }

  if (!copy) {
    return (
      <div className="text-gray-700 text-center py-10 text-2xl font-bold bg-white border-2 border-gray-900 rounded-lg mx-auto my-10 p-6" style={{fontFamily: 'Dosis, sans-serif'}}>
        Copy not found.
      </div>
    );
  }

  const currentAcPageData = copy.pages.find(
    (p) => p.pageNumber === acCurrentPage
  );
  const isPageChecked = !!currentAcPageData?.lastAnnotatedBy;
  const marksAwarded = currentAcPageData?.marksAwarded ?? "N/A";
  const comments = currentAcPageData?.comments?.join("\n") || "No comments.";

  // acFileProp / qpFileProp are memoized before early returns above.

  const totalMarksAwarded = copy.pages.reduce(
    (sum, page) => sum + (page.marksAwarded || 0),
    0
  );
  const totalPossibleMarks = copy.questionPaper?.totalMarks || "N/A";

  const openPdfWithAuth = async (url) => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) return;
    newWindow.document.write('<p style="font-family:sans-serif;padding:20px;color:#666;">Loading PDF…</p>');
    setLoadingPdfs(prev => new Set(prev).add(url));
    try {
      const axiosPath = url.replace(/^\/api/, '');
      const response = await api.get(axiosPath, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      newWindow.location.href = blobUrl;
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (err) {
      console.error('Failed to open PDF:', err);
      newWindow.close();
    } finally {
      setLoadingPdfs(prev => { const s = new Set(prev); s.delete(url); return s; });
    }
  };

  return (
    <div className="bg-white min-h-screen" style={{fontFamily: 'Dosis, sans-serif'}}>
      

      <div className="p-6 max-w-screen mx-auto">
        <header className="mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Viewing Copy for:{" "}
                <span className="text-[#1e3a8a]">
                  {copy.questionPaper?.title || "N/A"}
                </span>
              </h2>
              <p className="text-xl text-gray-900 font-bold">
                Student: {copy.student?.name || "N/A"} (
                <span className="text-gray-600">
                  {copy.student?.email || "N/A"}
                </span>
                ) {copy.student?.batch ? `${copy.student.batch}` : ""}
              </p>
              <div className="mt-2">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${
                  copy.status === 'evaluated' ? 'bg-green-100 text-green-800 border border-green-300' :
                  copy.status === 'examining' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                  copy.status === 're-evaluating' ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                  'bg-gray-100 text-gray-800 border border-gray-300'
                }`}>
                  Status: {copy.status.charAt(0).toUpperCase() + copy.status.slice(1)}
                </span>
              </div>
            </div>
            
            {/* Reopen Button - Only show for evaluated copies */}
            {copy.status === 'evaluated' && (
              <div className="ml-4">
                <button
                  onClick={handleReopenCopy}
                  disabled={isReopening}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-bold text-sm flex items-center gap-2"
                  title="Reopen this copy for re-evaluation by the examiner"
                >
                  {isReopening ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Reopening...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="h-5 w-5" />
                      Reopen for Re-evaluation
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="max-w-2xl mx-auto mb-6 text-center">
          <h3 className="text-lg font-bold text-gray-800 mb-2">Total Marks</h3>
          <div className="flex items-center justify-center gap-3">
            <div className="flex flex-col items-center">
              <span className="text-sm text-gray-600 font-medium">Awarded</span>
              <span className="text-3xl font-extrabold text-green-600">{totalMarksAwarded}</span>
            </div>
            <span className="text-2xl text-gray-400">/</span>
            <div className="flex flex-col items-center">
              <span className="text-sm text-gray-600 font-medium">Maximum</span>
              <span className="text-3xl font-extrabold text-gray-900">{totalPossibleMarks}</span>
            </div>
          </div>
          <div className="mt-2">
            <span className="inline-block px-4 py-1 rounded-full text-sm font-bold bg-blue-50 text-blue-800 border border-blue-200">
              {totalPossibleMarks > 0 ? Math.round((totalMarksAwarded / totalPossibleMarks) * 100) : 0}% Score
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-6">
          {/* Question Paper Link */}
          <div className="">
            <div className="flex items-center justify-between">
              {qpPdfUrl ? (
                <button
                  onClick={() => openPdfWithAuth(qpPdfUrl)}
                  disabled={loadingPdfs.has(qpPdfUrl)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: loadingPdfs.has(qpPdfUrl) ? 'not-allowed' : 'pointer' }}
                  className="inline-flex items-center opacity-100 disabled:opacity-60"
                >
                  {loadingPdfs.has(qpPdfUrl) ? (<><svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>Loading...</>) : 'Open Question Paper in New Tab'}
                </button>
              ) : (
                <div className="text-gray-600">Question paper link not available.</div>
              )}
            </div>
          </div>

          {/* Answer Copy and Evaluation - Side by Side */}
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Left: Answer Copy Section */}
            <div className="bg-white p-4 rounded-xl border-2 border-gray-900 flex flex-col lg:w-[70%] h-[calc(100vh-120px)]">
            
              <div className="relative w-full flex-grow rounded-lg  border-gray-900 bg-gray-100 overflow-auto flex items-center justify-center">
                {isAcLoading && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10 text-gray-900 font-bold">
                    <ArrowPathIcon className="animate-spin h-10 w-10 text-gray-900 mb-2" />
                    <span className="text-lg">Loading Answer Copy...</span>
                  </div>
                )}
                {acPdfUrl ? (
                  <div className="relative inline-block" ref={pageWrapperRef}>
                    <Document
                      file={acFileProp}
                      onLoadSuccess={onAcDocumentLoadSuccess}
                      onLoadError={onAcDocumentLoadError}
                    >
                      <Page
                        pageNumber={acCurrentPage}
                        scale={acZoomLevel}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    </Document>

                    {/* Render examiner's marks on the current page (read-only) */}
                    {copy && copy.pages && Array.isArray(copy.pages) && (() => {
                      const pageData = copy.pages.find(p => p && p.pageNumber === acCurrentPage);
                      if (pageData && pageData.pageMarks && Array.isArray(pageData.pageMarks) && pageData.pageMarks.length > 0) {
                        return pageData.pageMarks
                          .filter(mark => mark && typeof mark.value === 'number' && typeof mark.x === 'number' && typeof mark.y === 'number')
                          .map((mark, idx) => (
                          <div
                            key={idx}
                            className="absolute pointer-events-none"
                            style={{
                              left: `${mark.x}%`,
                              top: `${mark.y}%`,
                              transform: 'translate(-50%, -50%)',
                              zIndex: 20,
                              // ensure the mark is positioned relative to the page wrapper
                              position: 'absolute'
                            }}
                          >
                            <div className={`w-12 h-12 flex items-center justify-center rounded-full text-white text-sm font-bold shadow-lg ${mark.value > 0 ? 'bg-green-500' : 'bg-red-500'}`}>
                              {Number(mark.value % 1 === 0 ? mark.value : mark.value.toFixed(1))}
                            </div>
                          </div>
                        ));
                      }
                      return null;
                    })()}
                  </div>
                ) : (
                  <div className="text-gray-500 text-center p-4">
                    Answer Copy Not Found.
                  </div>
                )}
                
                {/* Marks are rendered inside the page wrapper to avoid duplication */}
              </div>
            </div>

            {/* Right: Evaluation and Controls */}
            <div className="bg-white p-4 rounded-xl border-2 border-gray-900 flex flex-col lg:w-[30%]">
            {/* Page Navigation */}
            <div className="mb-4 pb-4 border-b-2 border-gray-200">
              {/* <h3 className="text-lg font-bold text-gray-800 mb-3">Page Navigation</h3> */}
              <div className="flex flex-col gap-2">
                <div className="flex gap-2 justify-between items-center">
                  <button
                    onClick={() => setAcCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={acCurrentPage === 1}
                    className="flex px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 transition font-bold text-sm"
                  >
                    Prev
                  </button>
                  <span className="text-center text-base font-bold text-gray-800">
                  Page {acCurrentPage} / {numAcPages || "..."}
                </span>
                  <button
                    onClick={() =>
                      setAcCurrentPage((p) => Math.min(numAcPages, p + 1))
                    }
                    disabled={!numAcPages || acCurrentPage === numAcPages}
                    className="flex px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 transition font-bold text-sm"
                  >
                    Next
                  </button>
                </div>
                
              </div>
            </div>

            {/* Page Status */}
            {isPageChecked && (
              <div className="mb-4">
                <span className="flex items-center text-sm font-bold text-gray-900 bg-white border-2 border-gray-900 px-3 py-2 rounded-lg">
                  <CheckBadgeIcon className="h-5 w-5 mr-2" />
                  Page Checked
                </span>
              </div>
            )}

            {/* Marks Awarded */}
            <div className="mb-4 pb-4 border-b-2 border-gray-900">
              <label className="block text-sm font-bold text-gray-600 mb-2">
                Marks Awarded
              </label>
              <p className="w-full text-center px-3 py-2 border-2 border-gray-900 rounded-lg bg-gray-50 text-gray-900 text-xl font-bold">
                {marksAwarded}
              </p>
            </div>

            {/* Comments */}
            <div className="mb-4 pb-4 border-b-2 border-gray-900 flex-grow">
              <label className="block text-sm font-bold text-gray-600 mb-2">
                Comments for Page {acCurrentPage}
              </label>
              <div className="p-3 border-2 border-gray-900 rounded-lg bg-gray-50 text-gray-800 text-sm whitespace-pre-wrap overflow-y-auto max-h-32">
                {comments}
              </div>
            </div>

            {/* Zoom Controls */}
            <div className="mt-auto">
              <label className="block text-sm font-bold text-gray-600 mb-2">
                Zoom Controls
              </label>
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handleZoom("out")}
                  disabled={acZoomLevel <= MIN_ZOOM}
                  className="p-2 bg-white border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-gray-900 hover:text-white disabled:opacity-50 transition"
                  title="Zoom Out"
                >
                  <MagnifyingGlassMinusIcon className="h-5 w-5" />
                </button>
                <span className="text-sm font-bold text-gray-700 bg-white px-3 py-1 rounded-md border-2 border-gray-900 w-16 text-center">
                  {(acZoomLevel * 100).toFixed(0)}%
                </span>
                <button
                  onClick={() => handleZoom("in")}
                  disabled={acZoomLevel >= MAX_ZOOM}
                  className="p-2 bg-gray-900 text-white rounded-lg hover:bg-[#1e3a8a] disabled:opacity-50 transition"
                  title="Zoom In"
                >
                  <MagnifyingGlassPlusIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleZoom("reset")}
                  disabled={acZoomLevel === DEFAULT_ZOOM}
                  className="p-2 bg-white border-2 border-gray-900 text-gray-900 rounded-lg hover:bg-gray-900 hover:text-white disabled:opacity-50 transition"
                  title="Reset Zoom"
                >
                  <ArrowsPointingInIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
}
